import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Card, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Chip, IconButton, Tooltip, CircularProgress,
    Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button,
    TextField, Select, MenuItem, FormControl, InputLabel, Badge,
    Breadcrumbs, Link as MuiLink
} from '@mui/material';
import {
    DeleteForever as DeleteForeverIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Refresh as RefreshIcon,
    Person as PersonIcon,
    OpenInNew as OpenInNewIcon,
    HourglassEmpty as PendingIcon,
    NavigateNext as NavigateNextIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useNotify } from 'react-admin';
import axios from 'axios';
import config from '../config/config.json';

const host = config.production.host;
const getJwt = () => localStorage.getItem('feathers-jwt');

const STATUS_CONFIG = {
    pending: { label: 'In attesa', color: 'warning', icon: <PendingIcon sx={{ fontSize: 14 }} /> },
    processed: { label: 'Elaborata', color: 'success', icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
    rejected: { label: 'Rifiutata', color: 'error', icon: <CancelIcon sx={{ fontSize: 14 }} /> }
};

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('it-IT', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
    });
};

const DeleteRequestsPage = () => {
    const navigate = useNavigate();
    const notify = useNotify();

    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('all');
    const [processDialog, setProcessDialog] = useState({ open: false, request: null });
    const [processNote, setProcessNote] = useState('');
    const [processing, setProcessing] = useState(false);
    const [matchedUsers, setMatchedUsers] = useState({});

    const loadRequests = useCallback(async () => {
        try {
            setLoading(true);
            const jwt = getJwt();
            const params = { $sort: { createdAt: -1 }, $limit: 200 };
            if (filterStatus !== 'all') params.status = filterStatus;

            const resp = await axios.get(`${host}/tbl-delete-requests`, {
                headers: { Authorization: `Bearer ${jwt}` },
                params
            });
            const data = resp.data?.data || resp.data || [];
            setRequests(data);

            // Try to match emails to users
            const emails = [...new Set(data.map(r => r.email))];
            if (emails.length > 0) {
                try {
                    const usersResp = await axios.get(`${host}/users`, {
                        headers: { Authorization: `Bearer ${jwt}` },
                        params: { email: { $in: emails }, $limit: emails.length }
                    });
                    const users = usersResp.data?.data || usersResp.data || [];
                    const map = {};
                    users.forEach(u => { map[u.email] = u; });
                    setMatchedUsers(map);
                } catch (e) {
                    // ignore
                }
            }
        } catch (err) {
            console.error('Errore caricamento richieste:', err);
            notify('Errore nel caricamento delle richieste', { type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [filterStatus, notify]);

    useEffect(() => { loadRequests(); }, [loadRequests]);

    const openProcessDialog = (request) => {
        setProcessDialog({ open: true, request });
        setProcessNote('');
    };

    const closeProcessDialog = () => {
        setProcessDialog({ open: false, request: null });
        setProcessNote('');
    };

    const handleProcess = async (newStatus) => {
        const { request } = processDialog;
        if (!request) return;
        try {
            setProcessing(true);
            const jwt = getJwt();
            await axios.patch(`${host}/tbl-delete-requests/${request.id}`, {
                status: newStatus,
                processedAt: new Date().toISOString(),
                note: processNote.trim() || null
            }, { headers: { Authorization: `Bearer ${jwt}` } });
            notify(
                newStatus === 'processed' ? 'Richiesta segnata come elaborata' : 'Richiesta rifiutata',
                { type: 'success' }
            );
            closeProcessDialog();
            loadRequests();
        } catch (err) {
            console.error('Errore elaborazione richiesta:', err);
            notify('Errore durante l\'elaborazione', { type: 'error' });
        } finally {
            setProcessing(false);
        }
    };

    const pendingCount = requests.filter(r => r.status === 'pending').length;

    return (
        <Box sx={{ p: 3 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
                <MuiLink underline="hover" color="inherit" sx={{ cursor: 'pointer' }} onClick={() => navigate('/')}>
                    Dashboard
                </MuiLink>
                <Typography color="text.primary">Richieste Cancellazione Account</Typography>
            </Breadcrumbs>

            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <DeleteForeverIcon color="error" sx={{ fontSize: 32 }} />
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            Richieste Cancellazione Account
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Gestisci le richieste di cancellazione inviate dagli utenti
                        </Typography>
                    </Box>
                    {pendingCount > 0 && (
                        <Chip
                            label={`${pendingCount} in attesa`}
                            color="warning"
                            size="small"
                            icon={<PendingIcon />}
                            sx={{ fontWeight: 600 }}
                        />
                    )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Filtra per stato</InputLabel>
                        <Select
                            value={filterStatus}
                            label="Filtra per stato"
                            onChange={(e) => setFilterStatus(e.target.value)}
                        >
                            <MenuItem value="all">Tutti</MenuItem>
                            <MenuItem value="pending">In attesa</MenuItem>
                            <MenuItem value="processed">Elaborate</MenuItem>
                            <MenuItem value="rejected">Rifiutate</MenuItem>
                        </Select>
                    </FormControl>
                    <Tooltip title="Aggiorna">
                        <IconButton onClick={loadRequests} disabled={loading}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Alert se non ci sono pending */}
            {!loading && pendingCount === 0 && filterStatus === 'all' && (
                <Alert severity="success" sx={{ mb: 3 }}>
                    Nessuna richiesta di cancellazione in attesa di elaborazione.
                </Alert>
            )}

            {/* Table */}
            <Card sx={{ borderRadius: 2 }}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.50' }}>
                                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Utente</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Motivo</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Stato</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Data Richiesta</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Data Elaborazione</TableCell>
                                <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Azioni</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}>
                                        <CircularProgress size={32} />
                                    </TableCell>
                                </TableRow>
                            ) : requests.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                                        Nessuna richiesta trovata
                                    </TableCell>
                                </TableRow>
                            ) : (
                                requests.map((req) => {
                                    const status = STATUS_CONFIG[req.status] || STATUS_CONFIG.pending;
                                    const matchedUser = matchedUsers[req.email];
                                    return (
                                        <TableRow
                                            key={req.id}
                                            hover
                                            sx={{
                                                bgcolor: req.status === 'pending' ? 'rgba(255, 152, 0, 0.04)' : 'inherit',
                                                borderLeft: req.status === 'pending' ? '3px solid #ff9800' : '3px solid transparent'
                                            }}
                                        >
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {req.email}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {matchedUser ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Chip
                                                            icon={<PersonIcon />}
                                                            label={`${matchedUser.nome || ''} ${matchedUser.cognome || ''}`.trim() || matchedUser.username}
                                                            size="small"
                                                            variant="outlined"
                                                            color="primary"
                                                            onClick={() => navigate(`/users/${matchedUser.id}`)}
                                                            sx={{ cursor: 'pointer' }}
                                                        />
                                                        <Tooltip title="Apri profilo">
                                                            <IconButton size="small" onClick={() => navigate(`/users/${matchedUser.id}`)}>
                                                                <OpenInNewIcon sx={{ fontSize: 14 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                ) : (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Non registrato
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200 }}>
                                                    {req.motivo || <em>Nessun motivo indicato</em>}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={status.icon}
                                                    label={status.label}
                                                    color={status.color}
                                                    size="small"
                                                    sx={{ fontWeight: 600 }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{formatDate(req.createdAt)}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{formatDate(req.processedAt)}</Typography>
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>
                                                {req.status === 'pending' ? (
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() => openProcessDialog(req)}
                                                        sx={{ fontWeight: 600 }}
                                                    >
                                                        Gestisci
                                                    </Button>
                                                ) : (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {req.note || '—'}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>

            {/* Process Dialog */}
            <Dialog open={processDialog.open} onClose={closeProcessDialog} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DeleteForeverIcon color="error" />
                    Elabora Richiesta
                </DialogTitle>
                <DialogContent>
                    {processDialog.request && (
                        <Box>
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                Stai elaborando la richiesta di cancellazione per:
                                <br /><strong>{processDialog.request.email}</strong>
                            </Alert>

                            {processDialog.request.motivo && (
                                <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                                    <Typography variant="caption" color="text.secondary">Motivo indicato dall'utente:</Typography>
                                    <Typography variant="body2">{processDialog.request.motivo}</Typography>
                                </Box>
                            )}

                            {matchedUsers[processDialog.request?.email] && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                        Utente trovato nel sistema:
                                    </Typography>
                                    <Chip
                                        icon={<PersonIcon />}
                                        label={`${matchedUsers[processDialog.request.email].nome || ''} ${matchedUsers[processDialog.request.email].cognome || ''}`.trim() || matchedUsers[processDialog.request.email].username}
                                        color="primary"
                                        variant="outlined"
                                        onClick={() => navigate(`/users/${matchedUsers[processDialog.request.email].id}`)}
                                        sx={{ cursor: 'pointer' }}
                                    />
                                </Box>
                            )}

                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Note interne (opzionale)"
                                value={processNote}
                                onChange={(e) => setProcessNote(e.target.value)}
                                placeholder="Es: Account disattivato il 17/04/2025, dati rimossi..."
                                sx={{ mt: 1 }}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button onClick={closeProcessDialog} disabled={processing}>Annulla</Button>
                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={processing ? <CircularProgress size={16} /> : <CancelIcon />}
                        onClick={() => handleProcess('rejected')}
                        disabled={processing}
                    >
                        Rifiuta Richiesta
                    </Button>
                    <Button
                        variant="contained"
                        color="success"
                        startIcon={processing ? <CircularProgress size={16} /> : <CheckCircleIcon />}
                        onClick={() => handleProcess('processed')}
                        disabled={processing}
                    >
                        Segna come Elaborata
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default DeleteRequestsPage;
